import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Clone the request object
    let newReq = req.clone();
    const accessToken = authService.accessToken;
    let hasValidAccessToken = false;
    let sessionWasEnded = false;

    if (accessToken) {
        try {
            hasValidAccessToken = !AuthUtils.isTokenExpired(accessToken);
        } catch {
            hasValidAccessToken = false;
        }

        if (!hasValidAccessToken) {
            authService.signOut();
            sessionWasEnded = true;
            void router.navigateByUrl('/sign-in');
        }
    }

    // A background tab can issue a request in the short interval between the
    // shared token being removed and its storage event being handled. Do not
    // let that protected request reach the API without authentication.
    if (!hasValidAccessToken && isProtectedAppRequest(req.url, router.url)) {
        if (!sessionWasEnded) {
            authService.signOut();
        }
        void router.navigateByUrl('/sign-in');

        return throwError(
            () =>
                new HttpErrorResponse({
                    status: 401,
                    statusText: 'Unauthorized',
                    url: req.url,
                    error: 'The session is no longer active.',
                })
        );
    }

    // Request
    //
    // Add the Authorization header only when the token is valid. Protected
    // requests without a valid token have already been stopped above.
    if (hasValidAccessToken && !isKycServiceEndpoint(req.url)) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + accessToken),
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (
                error instanceof HttpErrorResponse &&
                error.status === 401 &&
                !isPublicAuthEndpoint(req.url) &&
                !isKycServiceEndpoint(req.url)
            ) {
                authService.signOut();
                void router.navigateByUrl('/sign-in');
            }

            return throwError(() => error);
        })
    );
};

const isPublicAuthEndpoint = (url: string): boolean =>
    url.includes('/auth/boss/token') ||
    url.includes('/auth/boss/set-password') ||
    url.includes('/auth/register-admin') ||
    url.includes('/auth/sign-in') ||
    url.includes('/auth/sign-up') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/eastel-wallet/wallet/balance') ||
    url.includes('/eastel-wallet/wallet/transaction-list') ||
    url.includes('/auth/reset-password');

const isProtectedAppRequest = (
    requestUrl: string,
    routeUrl: string
): boolean => {
    const publicRoutes = [
        '/',
        '/confirmation-required',
        '/forgot-password',
        '/reset-password',
        '/sign-in',
        '/log-in',
        '/sign-up',
        '/sign-out',
        '/home',
    ];
    const routePath = routeUrl.split('?')[0];
    const isPublicRoute = publicRoutes.some(
        (route) => routePath === route || routePath.startsWith(`${route}/`)
    );
    const isConfigurationRequest = requestUrl.includes('/config/config.');

    return (
        !isPublicRoute &&
        !isConfigurationRequest &&
        !isPublicAuthEndpoint(requestUrl) &&
        !isKycServiceEndpoint(requestUrl)
    );
};

const isKycServiceEndpoint = (url: string): boolean =>
    url.includes('/kyc/') ||
    url.includes('/compare_faces/') ||
    url.includes('/watermark/text');
